import React from 'react';
import { buildClassMemberNumberMap } from '../../utils/classMemberNumbers';
import type { ClassMemberGroupMeta, ClassMemberRow } from '../../utils/classMembersStorage';
import { displayClassMemberName } from '../../utils/classMemberNameDisplay';
import {
  PRESENTATION_COMM_MAX_MULTI_RESPONSES,
  type PresentationCommunicationResponse,
} from '../../services/presentationCommunicationService';
import {
  communicationChoiceColor,
  communicationOxBadgeStyle,
  evaluateCommunicationQuestionCorrectness,
  formatCommunicationChoiceSelection,
  isChoiceLikeCommunicationQuestion,
  parseCommunicationChoiceSelection,
  type CommunicationQuestion,
} from '../../utils/presentationCommunicationAnswers';
import ProgramSlideCommunicationQuestionModal from './ProgramSlideCommunicationQuestionModal';
import {
  loadPresentationCommunicationTeacherViewPrefs,
  savePresentationCommunicationTeacherViewPrefs,
  type CommunicationAccuracySortOrder,
  type CommunicationBreakdownSortOrder,
  type CommunicationResultViewMode,
  type CommunicationSortOrder,
} from '../../utils/presentationCommunicationTeacherViewPrefs';

export type {
  CommunicationAccuracySortOrder,
  CommunicationBreakdownSortOrder,
  CommunicationResultViewMode,
  CommunicationSortOrder,
};

function IconEyeClose() {
  return (
    <svg
      className="cp-program-slide-communication-option-eye is-closed"
      viewBox="0 0 21 11"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M20.1041 6.55677C20.178 6.68506 20.2259 6.82664 20.245 6.97342C20.2642 7.12021 20.2542 7.26934 20.2158 7.41228C20.1773 7.55522 20.111 7.68919 20.0208 7.80653C19.9305 7.92387 19.818 8.02228 19.6898 8.09615C19.5615 8.17002 19.4199 8.21789 19.2731 8.23705C19.1263 8.2562 18.9772 8.24625 18.8342 8.20777C18.6913 8.1693 18.5573 8.10304 18.44 8.01279C18.3227 7.92254 18.2242 7.81006 18.1504 7.68177L16.5698 4.91615C15.6878 5.47099 14.7369 5.90784 13.7413 6.21552L14.2373 9.18177C14.286 9.47603 14.216 9.77761 14.0424 10.0202C13.8689 10.2628 13.6062 10.4266 13.3119 10.4755C13.25 10.4864 13.1873 10.4918 13.1244 10.4915C12.8588 10.4912 12.6018 10.3969 12.399 10.2253C12.1961 10.0537 12.0606 9.81591 12.0163 9.55396L11.5335 6.66646C10.597 6.77021 9.65189 6.77021 8.71538 6.66646L8.23725 9.55677C8.19292 9.81904 8.0571 10.0571 7.85389 10.2287C7.65068 10.4004 7.39324 10.4944 7.12725 10.4943C7.0644 10.4946 7.00165 10.4892 6.93975 10.4783C6.79404 10.4538 6.65458 10.4008 6.52935 10.3223C6.40413 10.2439 6.29558 10.1416 6.20991 10.0212C6.12425 9.90075 6.06314 9.76465 6.03008 9.62063C5.99703 9.47661 5.99267 9.32748 6.01725 9.18177L6.51319 6.20802C5.51765 5.90034 4.56674 5.46349 3.68475 4.90865L2.10413 7.68177C1.95494 7.94086 1.70895 8.13006 1.42026 8.20777C1.13157 8.28548 0.823835 8.24533 0.564752 8.09615C0.305669 7.94696 0.11646 7.70097 0.0387496 7.41228C-0.0389607 7.12359 0.00119283 6.81586 0.150377 6.55677L1.87725 3.54271C1.29196 3.01415 0.749332 2.44017 0.25444 1.82615C0.0797291 1.59313 0.00219371 1.30143 0.0381419 1.01242C0.07409 0.723406 0.22072 0.459595 0.447191 0.276476C0.673661 0.0933563 0.962322 0.00519983 1.25246 0.0305493C1.54259 0.0558988 1.81159 0.192778 2.00288 0.412397C3.50663 2.27427 6.13725 4.49427 10.1273 4.49427C14.1173 4.49427 16.7479 2.27427 18.2516 0.412397C18.4398 0.182507 18.7113 0.036388 19.0068 0.00593092C19.3023 -0.0245262 19.5979 0.0631511 19.829 0.249828C20.0602 0.436505 20.2081 0.707025 20.2404 1.00235C20.2728 1.29767 20.1871 1.59381 20.0019 1.82615C19.5064 2.44025 18.9632 3.01422 18.3773 3.54271L20.1041 6.55677Z"
        fill="currentColor"
      />
    </svg>
  );
}

function IconEyeOpen() {
  return (
    <svg
      className="cp-program-slide-communication-option-eye is-open"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M23.5311 11.5434C23.4964 11.4675 22.6752 9.64406 20.8611 7.83C18.4339 5.40656 15.3749 4.125 11.9999 4.125C8.62488 4.125 5.56582 5.40656 3.14144 7.83C1.32738 9.64406 0.50613 11.4675 0.46863 11.5434C0.405117 11.6874 0.372314 11.8431 0.372314 12.0005C0.372314 12.1579 0.405117 12.3135 0.46863 12.4575C0.503317 12.5344 1.32457 14.3569 3.13957 16.1709C5.56582 18.5944 8.62488 19.875 11.9999 19.875C15.3749 19.875 18.4339 18.5944 20.8574 16.1709C22.6724 14.3569 23.4936 12.5344 23.5283 12.4575C23.5923 12.3137 23.6256 12.1581 23.626 12.0008C23.6265 11.8434 23.5942 11.6876 23.5311 11.5434ZM19.213 14.6372C17.2002 16.6191 14.7739 17.625 11.9999 17.625C9.22582 17.625 6.79957 16.6191 4.78957 14.6362C3.99864 13.8537 3.31829 12.9667 2.76738 12C3.31845 11.0337 3.99879 10.1471 4.78957 9.36469C6.8005 7.38094 9.22582 6.375 11.9999 6.375C14.7739 6.375 17.1993 7.38094 19.2102 9.36469C20.0011 10.147 20.6814 11.0336 21.2324 12C20.6814 12.9666 20.001 13.8536 19.2102 14.6362L19.213 14.6372ZM11.9999 7.875C11.184 7.875 10.3865 8.11693 9.70815 8.57019C9.0298 9.02345 8.50109 9.66769 8.18888 10.4214C7.87667 11.1752 7.79498 12.0046 7.95414 12.8047C8.11331 13.6049 8.50617 14.3399 9.08306 14.9168C9.65996 15.4937 10.395 15.8866 11.1951 16.0457C11.9953 16.2049 12.8247 16.1232 13.5784 15.811C14.3322 15.4988 14.9764 14.9701 15.4297 14.2917C15.883 13.6134 16.1249 12.8158 16.1249 12C16.1236 10.9064 15.6886 9.85787 14.9153 9.08455C14.142 8.31124 13.0935 7.87624 11.9999 7.875ZM11.9999 13.875C11.629 13.875 11.2665 13.765 10.9582 13.559C10.6498 13.353 10.4095 13.0601 10.2676 12.7175C10.1257 12.3749 10.0886 11.9979 10.1609 11.6342C10.2333 11.2705 10.4118 10.9364 10.6741 10.6742C10.9363 10.412 11.2704 10.2334 11.6341 10.161C11.9978 10.0887 12.3748 10.1258 12.7174 10.2677C13.06 10.4096 13.3529 10.65 13.5589 10.9583C13.7649 11.2666 13.8749 11.6292 13.8749 12C13.8749 12.4973 13.6773 12.9742 13.3257 13.3258C12.9741 13.6775 12.4972 13.875 11.9999 13.875Z"
        fill="currentColor"
      />
    </svg>
  );
}

function normalizeLabelCompare(value: string): string {
  return String(value ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

function compareAlphaKo(a: string, b: string): number {
  const aNum = parseInt(a, 10);
  const bNum = parseInt(b, 10);
  const aIsNum = !Number.isNaN(aNum);
  const bIsNum = !Number.isNaN(bNum);
  if (aIsNum && bIsNum) return aNum - bNum;
  if (aIsNum) return -1;
  if (bIsNum) return 1;
  return a.localeCompare(b, 'ko');
}

function formatCommunicationCorrectAnswer(
  question: CommunicationQuestion | null,
  legacyAnswerText: string
): string {
  if (isChoiceLikeCommunicationQuestion(question)) {
    const choices = question?.choices ?? [];
    const idxs = question?.correctChoiceIndexes ?? [];
    if (idxs.length === 0) return '';
    return idxs
      .map((i) => {
        const text = (choices[i]?.text ?? '').trim();
        return text || `${i + 1}번`;
      })
      .filter(Boolean)
      .join(', ');
  }
  if (question?.type === 'short') {
    return (question.answers ?? []).map((a) => a.trim()).filter(Boolean).join(', ');
  }
  if (question?.type === 'essay') return '';
  return String(legacyAnswerText ?? '')
    .split('#')
    .map((p) => p.trim())
    .filter(Boolean)
    .join(', ');
}

/** 선택형 응답/정답: 번호 배지 색상으로 표시 (텍스트만·숫자만 대신) */
function renderChoiceAnswerBadges(
  bodyText: string,
  question: CommunicationQuestion | null,
  emptyFallback = '응답 없음'
): React.ReactNode {
  const body = String(bodyText ?? '').trim();
  if (!question || !isChoiceLikeCommunicationQuestion(question)) {
    return body || emptyFallback;
  }
  const isOx = question.type === 'ox';
  const indexes = parseCommunicationChoiceSelection(body, question.choices ?? []);
  if (indexes.length === 0) {
    return body || emptyFallback;
  }
  return (
    <span className="cp-comm-choice-answer-badges" aria-label={body}>
      {indexes.map((idx) => (
        <span
          key={idx}
          className="cp-comm-choice-answer-badge"
          style={
            isOx
              ? communicationOxBadgeStyle(idx === 0)
              : { background: communicationChoiceColor(idx) }
          }
        >
          {isOx ? (idx === 0 ? 'O' : 'X') : idx + 1}
        </span>
      ))}
    </span>
  );
}

function isCommunicationLabelCorrect(
  label: string,
  question: CommunicationQuestion | null,
  legacyAnswerText: string,
  showAnswer: boolean
): boolean {
  if (!showAnswer) return false;
  const correct = formatCommunicationCorrectAnswer(question, legacyAnswerText);
  if (!correct) return false;
  const nl = normalizeLabelCompare(label);
  return correct
    .split(',')
    .map((a) => a.trim())
    .filter(Boolean)
    .some((p) => normalizeLabelCompare(p) === nl);
}

function responseTimeMs(updatedAt: string | undefined): number {
  if (!updatedAt) return 0;
  const t = Date.parse(updatedAt);
  return Number.isFinite(t) ? t : 0;
}

export type ProgramSlideCommunicationPanelProps = {
  mode: 'teacher' | 'student';
  promptText: string;
  onPromptChange?: (value: string) => void;
  answerText?: string;
  onAnswerChange?: (value: string) => void;
  question?: CommunicationQuestion | null;
  onQuestionChange?: (question: CommunicationQuestion | null) => void;
  onSaveQuestion?: (
    question: CommunicationQuestion,
    promptText: string,
    answerText: string
  ) => void | Promise<void>;
  onSavePrompt?: () => void | Promise<void>;
  savingPrompt?: boolean;
  saveDisabled?: boolean;
  /** 질문 저장 실패 메시지(모달 표시용) */
  questionSaveError?: string | null;
  onClearQuestionSaveError?: () => void;
  members: ClassMemberRow[];
  responses: PresentationCommunicationResponse[];
  /** 교사: 멤버 그룹 선택(왼쪽 상단) */
  groups?: ClassMemberGroupMeta[];
  selectedGroupId?: string | null;
  onSelectGroup?: (groupId: string) => void;
  groupMemberCounts?: Record<string, number>;
  /** 교사: 원탭 모니터링 모달 열기 */
  onOpenMonitor?: () => void;
  /** onOpenMonitor가 없을 때(모니터링 모달 자신 등) 자리만 유지하는 비활성 모니터링 버튼 표시 */
  showDisabledMonitorButton?: boolean;
  /** 교사: 개별 디바이스 프레젠테이션 연결(QR 안내) — 모니터링 버튼 오른쪽 '연결' */
  onOpenConnect?: () => void;
  connectLoading?: boolean;
  /** true면 문제 상세 모달을 항상 보기모드로만 열고 편집모드 전환을 막음(모니터링 모달 등) */
  questionDetailReadOnly?: boolean;
  /** 교사: 질문 탐색·삭제 */
  questionNav?: {
    canPrev: boolean;
    canNext: boolean;
    canDelete: boolean;
    /** 1부터 시작하는 현재 질문 번호 */
    currentIndex: number;
    /** 전체 질문 수(N) */
    totalCount: number;
    /** true면 1/N 표시를 클릭 불가로 유지하되 비활성 스타일(흐림) 없이 흰 원형 그대로 표시 */
    positionDisplayOnly?: boolean;
    onPrev: () => void;
    onNext: () => void;
    onDeleteRequest: () => void;
    /** 있으면「다음」오른쪽에 새 질문 추가(+) 버튼 표시(모니터링 팝업) */
    canAdd?: boolean;
    onAdd?: () => void;
  } | null;
  deleteQuestionConfirmOpen?: boolean;
  onCancelDeleteQuestion?: () => void;
  onConfirmDeleteQuestion?: () => void;
  deletingQuestion?: boolean;
  /** student */
  memberName?: string;
  onMemberNameChange?: (value: string) => void;
  entryNumber?: string;
  onEntryNumberChange?: (value: string) => void;
  draftBody?: string;
  onDraftBodyChange?: (value: string) => void;
  onSubmitResponse?: () => void;
  submitting?: boolean;
  submitMessage?: string;
  submitError?: string;
  /** student: 교사가 재생 중일 때만 true. false면 질문 대기 상태 */
  isOpen?: boolean;
  /** student: 복수 응답 모드(최대 2회). false면 멤버당 1회만 */
  multiResponse?: boolean;
  /**
   * student: 입장하기 시 이름·입장번호 서버 검증.
   * 성공 시에만 질문 페이지로 진입. 실패 시 throw(메시지 표시).
   */
  onEnterRequest?: () => void | Promise<void>;
  /** teacher: 재생 컨트롤(모니터링 버튼 오른쪽) */
  playControl?: {
    isOpen: boolean;
    multiResponse: boolean;
    canPlay: boolean;
    playing?: boolean;
    /** canPlay가 false일 때 표시할 안내(예: 저장되지 않은 변경사항이 있을 때) */
    disabledReason?: string;
    onToggleMultiResponse: () => void;
    onPlay: () => void;
    onStop: () => void;
  } | null;
  /** 교사: 프레젠테이션의 모든 질문·응답을 엑셀(.xlsx)/PDF로 내보내기(제공될 때만 버튼 표시) */
  onExportCsv?: () => void | Promise<void>;
  onExportPdf?: () => void | Promise<void>;
  exportingCsv?: boolean;
  exportingPdf?: boolean;
};

function responseByMemberId(
  responses: PresentationCommunicationResponse[]
): Map<string, PresentationCommunicationResponse> {
  const map = new Map<string, PresentationCommunicationResponse>();
  for (const r of responses) {
    map.set(r.memberId, r);
  }
  return map;
}

/** 복수 응답: 멤버별로 제출 순서(오래된 순)로 묶는다. */
function groupResponsesByMemberId(
  responses: PresentationCommunicationResponse[]
): Map<string, PresentationCommunicationResponse[]> {
  const map = new Map<string, PresentationCommunicationResponse[]>();
  for (const r of responses) {
    const list = map.get(r.memberId);
    if (list) list.push(r);
    else map.set(r.memberId, [r]);
  }
  Array.from(map.values()).forEach((list) => {
    list.sort(
      (a: PresentationCommunicationResponse, b: PresentationCommunicationResponse) =>
        responseTimeMs(a.updatedAt) - responseTimeMs(b.updatedAt)
    );
  });
  return map;
}

function IconAirplane() {
  return (
    <svg className="cp-comm-airplane-icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" fill="currentColor" />
    </svg>
  );
}

function IconChoiceCheck() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 13l4 4L19 7" />
    </svg>
  );
}

function hasAnySeat(members: ClassMemberRow[]): boolean {
  return members.some((m) => m.seatPosition != null);
}

function shuffleInPlace<T>(list: T[]): T[] {
  for (let i = list.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    const tmp = list[i]!;
    list[i] = list[j]!;
    list[j] = tmp;
  }
  return list;
}

/** 포인트·코인·아이템 자리모드와 동일: 16트랙·2칸 스팬·바깥 trim·통로 압축 */
function buildSeatViewData(
  members: ClassMemberRow[],
  isSeatRotated: boolean
): {
  positioned: Array<{ member: ClassMemberRow; gridRow: number; gridCol: number }>;
  unseated: ClassMemberRow[];
  rowTemplate: string;
  colTemplate: string;
} {
  const seated = members.filter((m) => m.seatPosition);
  const unseated = members.filter((m) => !m.seatPosition);
  if (!seated.length) {
    return { positioned: [], unseated, rowTemplate: '', colTemplate: '' };
  }

  const GRID_TRACKS = 16;
  const ROTATE_MAX_INDEX = 14;
  const usedRows = new Set<number>();
  const usedCols = new Set<number>();

  const rotatedSeats = seated.map((member) => {
    const seatRow = member.seatPosition!.row;
    const seatCol = member.seatPosition!.col;
    const rotatedRow = isSeatRotated ? ROTATE_MAX_INDEX - seatRow : seatRow;
    const rotatedCol = isSeatRotated ? ROTATE_MAX_INDEX - seatCol : seatCol;
    [rotatedRow, rotatedRow + 1].forEach((r) => {
      if (r >= 0 && r < GRID_TRACKS) usedRows.add(r);
    });
    [rotatedCol, rotatedCol + 1].forEach((c) => {
      if (c >= 0 && c < GRID_TRACKS) usedCols.add(c);
    });
    return { member, rotatedRow, rotatedCol };
  });

  const trimStartRow = Math.min(...Array.from(usedRows));
  const trimEndRow = Math.max(...Array.from(usedRows));
  const trimStartCol = Math.min(...Array.from(usedCols));
  const trimEndCol = Math.max(...Array.from(usedCols));

  const rowTemplate = Array.from(
    { length: Math.max(1, trimEndRow - trimStartRow + 1) },
    /* 자리 1칸 = 2트랙 → 트랙 최소 높이를 카드 높이의 절반 이상으로 두어 위아래 겹침 방지 */
    (_, idx) => (usedRows.has(trimStartRow + idx) ? 'minmax(var(--cp-comm-seat-track-min, 62px), 1fr)' : 'minmax(0, 0.5fr)')
  ).join(' ');

  const colTemplate = Array.from(
    { length: Math.max(1, trimEndCol - trimStartCol + 1) },
    (_, idx) => (usedCols.has(trimStartCol + idx) ? '1fr' : '0.28fr')
  ).join(' ');

  const positioned = rotatedSeats.map(({ member, rotatedRow, rotatedCol }) => ({
    member,
    gridRow: rotatedRow - trimStartRow + 1,
    gridCol: rotatedCol - trimStartCol + 1,
  }));

  return { positioned, unseated, rowTemplate, colTemplate };
}

/** 에듀코디 ResultBadge와 동일한 O/X 스트로크 아이콘 */
function renderOxIcon(correct: boolean) {
  return correct ? (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" fill="none" />
    </svg>
  ) : (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M6 6l12 12M18 6L6 18"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

/** 정답/오답 원형 O·X 배지 — 리모컨 응답 타일에서도 재사용 */
export function ProgramOxResultBadge({ correct }: { correct: boolean }) {
  return renderOxBadge(correct);
}

function renderOxBadge(correct: boolean) {
  return (
    <span
      className={'cp-program-slide-communication-ox' + (correct ? ' is-o' : ' is-x')}
      aria-label={correct ? '정답' : '오답'}
    >
      {renderOxIcon(correct)}
    </span>
  );
}

function renderMemberCard(params: {
  member: ClassMemberRow;
  num: number;
  responses: PresentationCommunicationResponse[];
  pageIndex: number;
  onPageChange: (nextIndex: number) => void;
  showName: boolean;
  showResponse: boolean;
  showAnswer: boolean;
  question: CommunicationQuestion | null;
  answerText: string;
}): React.ReactNode {
  const {
    member,
    num,
    responses,
    pageIndex,
    onPageChange,
    showName,
    showResponse,
    showAnswer,
    question,
    answerText,
  } = params;
  const total = responses.length;
  const clampedIndex = total > 0 ? Math.min(Math.max(pageIndex, 0), total - 1) : 0;
  const body = (responses[clampedIndex]?.bodyText ?? '').trim();
  const displayName = showName ? displayClassMemberName(member).trim() || '이름 없음' : '—';
  const displayBody = showResponse
    ? renderChoiceAnswerBadges(body, question, '응답 없음')
    : '····';
  const correctness = showAnswer
    ? evaluateCommunicationQuestionCorrectness(body, question, answerText)
    : null;
  return (
    <article
      className={
        'cp-program-slide-communication-card' +
        (body && showResponse ? '' : ' is-empty') +
        (correctness === true ? ' is-correct' : '') +
        (correctness === false ? ' is-incorrect' : '')
      }
    >
      {correctness != null ? renderOxBadge(correctness) : null}
      <header className="cp-program-slide-communication-card-head">
        <span className="cp-program-slide-communication-card-num">
          {String(num).padStart(2, '0')}
        </span>
        <span
          className="cp-program-slide-communication-card-name"
          title={showName ? displayClassMemberName(member) : undefined}
        >
          {displayName}
        </span>
      </header>
      <div className="cp-program-slide-communication-card-body">{displayBody}</div>
      {showResponse && total > 1 ? (
        <div className="cp-comm-answer-pager">
          <button
            type="button"
            className="cp-comm-answer-pager-btn"
            disabled={clampedIndex <= 0}
            onClick={(e) => {
              e.stopPropagation();
              onPageChange(clampedIndex - 1);
            }}
            aria-label="이전 응답"
          >
            ‹
          </button>
          <span className="cp-comm-answer-pager-count">
            {clampedIndex + 1}/{total}
          </span>
          <button
            type="button"
            className="cp-comm-answer-pager-btn"
            disabled={clampedIndex >= total - 1}
            onClick={(e) => {
              e.stopPropagation();
              onPageChange(clampedIndex + 1);
            }}
            aria-label="다음 응답"
          >
            ›
          </button>
        </div>
      ) : null}
    </article>
  );
}

const ProgramSlideCommunicationPanel: React.FC<ProgramSlideCommunicationPanelProps> = ({
  mode,
  promptText,
  onPromptChange,
  answerText = '',
  onAnswerChange,
  question = null,
  onQuestionChange,
  onSaveQuestion,
  onSavePrompt,
  savingPrompt,
  saveDisabled: _saveDisabled,
  questionSaveError = null,
  onClearQuestionSaveError,
  members,
  responses,
  groups = [],
  selectedGroupId = null,
  onSelectGroup,
  groupMemberCounts: _groupMemberCounts,
  onOpenMonitor,
  onOpenConnect,
  connectLoading,
  showDisabledMonitorButton = false,
  questionDetailReadOnly = false,
  questionNav = null,
  deleteQuestionConfirmOpen = false,
  onCancelDeleteQuestion,
  onConfirmDeleteQuestion,
  deletingQuestion = false,
  memberName = '',
  onMemberNameChange,
  entryNumber = '',
  onEntryNumberChange,
  draftBody = '',
  onDraftBodyChange,
  onSubmitResponse,
  submitting,
  submitMessage,
  submitError,
  isOpen = true,
  multiResponse = false,
  onEnterRequest,
  playControl = null,
  onExportCsv,
  onExportPdf,
  exportingCsv = false,
  exportingPdf = false,
}) => {
  const byMember = React.useMemo(() => responseByMemberId(responses), [responses]);
  const responsesByMemberAll = React.useMemo(() => groupResponsesByMemberId(responses), [responses]);
  const [memberResponsePageIndex, setMemberResponsePageIndex] = React.useState<Record<string, number>>({});
  const setMemberResponsePage = React.useCallback((memberId: string, nextIndex: number) => {
    setMemberResponsePageIndex((prev) => ({ ...prev, [memberId]: nextIndex }));
  }, []);
  const [hasEntered, setHasEntered] = React.useState(false);
  const [entering, setEntering] = React.useState(false);
  const [enterError, setEnterError] = React.useState('');
  const seatsAvailable = hasAnySeat(members);

  React.useEffect(() => {
    setEnterError('');
  }, [memberName, entryNumber]);
  const sortedGroups = React.useMemo(
    () => [...groups].sort((a, b) => a.sortOrder - b.sortOrder || a.id.localeCompare(b.id)),
    [groups]
  );
  const hasSavedPrompt = (promptText || '').trim().length > 0;
  const [questionModalOpen, setQuestionModalOpen] = React.useState(false);
  const [questionModalMode, setQuestionModalMode] = React.useState<'edit' | 'view'>('edit');

  const openQuestionModal = (mode: 'edit' | 'view') => {
    onClearQuestionSaveError?.();
    setQuestionModalMode(mode);
    setQuestionModalOpen(true);
  };

  /** 로컬에 저장된 보기 옵션(없으면 멤버별 응답 결과 + 번호순) — 마운트 시 1회만 읽음 */
  const [bootViewPrefs] = React.useState(() => loadPresentationCommunicationTeacherViewPrefs());
  const [sortOrder, setSortOrder] = React.useState<CommunicationSortOrder>(() => {
    if (bootViewPrefs.sortOrder === 'seat' && !hasAnySeat(members)) return 'number';
    return bootViewPrefs.sortOrder;
  });
  const [resultViewModePref, setResultViewMode] = React.useState<CommunicationResultViewMode>(
    bootViewPrefs.resultViewMode
  );
  /** 서술형은 정답 채점이 없음 → '정답·오답 결과' 대신 멤버별 응답으로 보여줌(저장된 설정은 유지) */
  const isEssayQuestion = question?.type === 'essay';
  const resultViewMode: CommunicationResultViewMode =
    isEssayQuestion && resultViewModePref === 'accuracy' ? 'students' : resultViewModePref;
  const [accuracySortOrder, setAccuracySortOrder] = React.useState<CommunicationAccuracySortOrder>(
    bootViewPrefs.accuracySortOrder
  );
  const [breakdownSortOrder, setBreakdownSortOrder] =
    React.useState<CommunicationBreakdownSortOrder>(bootViewPrefs.breakdownSortOrder);
  const [randomSeed, setRandomSeed] = React.useState(0);
  const [showResponse, setShowResponse] = React.useState(bootViewPrefs.showResponse);
  const [showName, setShowName] = React.useState(bootViewPrefs.showName);
  const [showAnswer, setShowAnswer] = React.useState(bootViewPrefs.showAnswer);
  const [isSeatRotated, setIsSeatRotated] = React.useState(bootViewPrefs.isSeatRotated);

  // 에듀코디: -/+ 로 학생 카드(번호순~자리배치) 내부 폰트 크기 조절
  const [studentFontScale, setStudentFontScale] = React.useState(bootViewPrefs.studentFontScale);
  const FONT_SCALE_MIN = 0.8;
  // 기존 상한 1.3의 약 2배 (에듀코디 blockSize 상한 2.5와 유사)
  const FONT_SCALE_MAX = 2.6;
  const FONT_SCALE_STEP = 0.1;

  const adjustStudentFontScale = React.useCallback((delta: number) => {
    setStudentFontScale((v) => {
      const nextRaw = v + delta;
      const clamped = Math.min(FONT_SCALE_MAX, Math.max(FONT_SCALE_MIN, nextRaw));
      // step 단위로 스냅
      const snapped = Math.round(clamped / FONT_SCALE_STEP) * FONT_SCALE_STEP;
      return Math.min(FONT_SCALE_MAX, Math.max(FONT_SCALE_MIN, snapped));
    });
  }, []);

  React.useEffect(() => {
    if (!showName && sortOrder === 'number') {
      setSortOrder('response');
    }
  }, [showName, sortOrder]);

  /** 자리 정보가 없으면 자리배치 정렬을 번호순으로 되돌린다 */
  React.useEffect(() => {
    if (sortOrder === 'seat' && !seatsAvailable) {
      setSortOrder('number');
    }
  }, [sortOrder, seatsAvailable]);

  /** 교사 보기 옵션 변경을 로컬에 저장(질문 페이지 재진입 시 복원) */
  React.useEffect(() => {
    if (mode !== 'teacher') return;
    savePresentationCommunicationTeacherViewPrefs({
      resultViewMode: resultViewModePref,
      sortOrder,
      accuracySortOrder,
      breakdownSortOrder,
      showResponse,
      showName,
      showAnswer,
      isSeatRotated,
      studentFontScale,
    });
  }, [
    mode,
    resultViewModePref,
    sortOrder,
    accuracySortOrder,
    breakdownSortOrder,
    showResponse,
    showName,
    showAnswer,
    isSeatRotated,
    studentFontScale,
  ]);

  // 에듀코디: 항목별에서 응답 토글 잠금, 정답·오답에서 정답 토글 잠금
  const responseToggleLocked = resultViewMode === 'breakdown';
  const answerToggleLocked = resultViewMode === 'accuracy';

  const memberNumberById = React.useMemo(() => buildClassMemberNumberMap(members), [members]);

  const seatViewData = React.useMemo(
    () => buildSeatViewData(members, isSeatRotated),
    [members, isSeatRotated]
  );

  type RespondedEntry = {
    member: ClassMemberRow;
    body: string;
    updatedAt: string;
    num: number;
  };

  const respondedEntries = React.useMemo((): RespondedEntry[] => {
    const list: RespondedEntry[] = [];
    for (const member of members) {
      const resp = byMember.get(member.id);
      const body = (resp?.bodyText ?? '').trim();
      if (!body) continue;
      list.push({
        member,
        body,
        updatedAt: resp?.updatedAt ?? '',
        num: memberNumberById.get(member.id) ?? 0,
      });
    }
    return list;
  }, [members, byMember, memberNumberById]);

  const handleExportCommunicationCsv = React.useCallback(() => {
    if (!onExportCsv || exportingCsv) return;
    void onExportCsv();
  }, [onExportCsv, exportingCsv]);

  const handleExportCommunicationPdf = React.useCallback(() => {
    if (!onExportPdf || exportingPdf) return;
    void onExportPdf();
  }, [onExportPdf, exportingPdf]);

  const accuracyStats = React.useMemo(() => {
    const correct: RespondedEntry[] = [];
    const incorrect: RespondedEntry[] = [];
    for (const entry of respondedEntries) {
      const result = evaluateCommunicationQuestionCorrectness(
        entry.body,
        question,
        answerText
      );
      if (result === true) correct.push(entry);
      else if (result === false) incorrect.push(entry);
    }
    return { correct, incorrect };
  }, [respondedEntries, question, answerText]);

  const groupAccuracyAnswers = React.useCallback(
    (entries: RespondedEntry[]) => {
      const answerMap = new Map<
        string,
        { answer: string; count: number; latest: number; responders: RespondedEntry[] }
      >();
      for (const entry of entries) {
        const formatted = entry.body;
        const prev = answerMap.get(formatted);
        const t = responseTimeMs(entry.updatedAt);
        if (prev) {
          prev.count += 1;
          prev.responders.push(entry);
          if (t > prev.latest) prev.latest = t;
        } else {
          answerMap.set(formatted, {
            answer: formatted,
            count: 1,
            latest: t,
            responders: [entry],
          });
        }
      }
      const answers = Array.from(answerMap.values());
      if (accuracySortOrder === 'alphabetical') {
        return answers.sort((a, b) => compareAlphaKo(a.answer, b.answer));
      }
      return answers.sort((a, b) => b.latest - a.latest);
    },
    [accuracySortOrder]
  );

  const incorrectAnswers = React.useMemo(
    () => groupAccuracyAnswers(accuracyStats.incorrect),
    [groupAccuracyAnswers, accuracyStats.incorrect]
  );

  const correctAnswers = React.useMemo(
    () => groupAccuracyAnswers(accuracyStats.correct),
    [groupAccuracyAnswers, accuracyStats.correct]
  );

  const breakdownStats = React.useMemo(() => {
    type Bucket = { label: string; count: number; responders: RespondedEntry[]; latest: number };
    const map = new Map<string, Bucket>();

    for (const entry of respondedEntries) {
      const label = entry.body;
      const key = normalizeLabelCompare(label);
      const prev = map.get(key);
      const t = responseTimeMs(entry.updatedAt);
      if (prev) {
        prev.count += 1;
        prev.responders.push(entry);
        if (t > prev.latest) prev.latest = t;
      } else {
        map.set(key, { label, count: 1, responders: [entry], latest: t });
      }
    }

    const counts = Array.from(map.values());
    if (breakdownSortOrder === 'correctness') {
      return counts.sort((a, b) => {
        const aOk = isCommunicationLabelCorrect(a.label, question, answerText, showAnswer);
        const bOk = isCommunicationLabelCorrect(b.label, question, answerText, showAnswer);
        if (aOk && !bOk) return -1;
        if (!aOk && bOk) return 1;
        return b.count - a.count;
      });
    }
    if (breakdownSortOrder === 'alphabetical') {
      return counts.sort((a, b) => compareAlphaKo(a.label, b.label));
    }
    if (breakdownSortOrder === 'count') {
      return counts.sort((a, b) => {
        if (a.count !== b.count) return b.count - a.count;
        return compareAlphaKo(a.label, b.label);
      });
    }
    return counts.sort((a, b) => b.latest - a.latest);
  }, [respondedEntries, breakdownSortOrder, question, answerText, showAnswer]);

  const sortedMembers = React.useMemo(() => {
    const list = [...members];
    if (sortOrder === 'seat') {
      return list;
    }
    if (sortOrder === 'response') {
      list.sort((a, b) => {
        const ra = byMember.get(a.id);
        const rb = byMember.get(b.id);
        const ta = responseTimeMs(ra?.updatedAt);
        const tb = responseTimeMs(rb?.updatedAt);
        if (ta && tb) return tb - ta;
        if (ta && !tb) return -1;
        if (!ta && tb) return 1;
        return (memberNumberById.get(a.id) ?? 0) - (memberNumberById.get(b.id) ?? 0);
      });
      return list;
    }
    if (sortOrder === 'random') {
      void randomSeed;
      return shuffleInPlace(list);
    }
    list.sort(
      (a, b) => (memberNumberById.get(a.id) ?? 0) - (memberNumberById.get(b.id) ?? 0)
    );
    return list;
  }, [members, sortOrder, byMember, memberNumberById, randomSeed]);

  const seatMode =
    resultViewMode === 'students' &&
    sortOrder === 'seat' &&
    seatViewData.positioned.length > 0;

  /* 번호순·응답순·무작위: 학생 블록을 화면 세로에 맞춤(블록 높이 ≥ FIT_MIN_BLOCK_PX 일 때만, 아니면 스크롤) */
  const FIT_MIN_BLOCK_PX = 64;
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const studentsRef = React.useRef<HTMLDivElement | null>(null);
  const [fitRows, setFitRows] = React.useState(0);
  const listFitCandidate = resultViewMode === 'students' && !seatMode && sortedMembers.length > 0;
  React.useLayoutEffect(() => {
    if (!listFitCandidate) {
      setFitRows(0);
      return undefined;
    }
    const root = rootRef.current;
    const studentsEl = studentsRef.current;
    if (!root || !studentsEl) return undefined;
    const scroller =
      (root.closest('.cp-program-slide-body-scroll') as HTMLElement | null) ?? root.parentElement ?? root;
    const measure = () => {
      const board = studentsEl.querySelector('.cp-program-slide-communication-board') as HTMLElement | null;
      if (!board) return;
      const cs = window.getComputedStyle(board);
      const cols = Math.max(1, cs.gridTemplateColumns.split(' ').filter(Boolean).length);
      const gap = parseFloat(cs.rowGap) || 10;
      const rows = Math.ceil(sortedMembers.length / cols);
      const sRect = scroller.getBoundingClientRect();
      const top = studentsEl.getBoundingClientRect().top - sRect.top + scroller.scrollTop;
      const avail = scroller.clientHeight - top - 14 /* students padding-top */ - 12 /* board padding */ - 18 /* root padding-bottom */;
      const blockH = (avail - gap * (rows - 1)) / rows;
      const next = blockH >= FIT_MIN_BLOCK_PX ? rows : 0;
      setFitRows((prev) => (prev === next ? prev : next));
    };
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(scroller);
    ro?.observe(root);
    window.addEventListener('resize', measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [listFitCandidate, sortedMembers.length]);
  const listFit = listFitCandidate && fitRows > 0;

  const handleSortClick = (next: CommunicationSortOrder) => {
    if (next === 'number' && !showName) return;
    if (next === 'random') setRandomSeed((s) => s + 1);
    setSortOrder(next);
  };

  const handleResultViewModeChange = (next: CommunicationResultViewMode) => {
    setResultViewMode(next);
  };

  const showTeacherOptions = mode === 'teacher';
  const showGroupPicker = showTeacherOptions && sortedGroups.length > 0;
  const [groupDropdownOpen, setGroupDropdownOpen] = React.useState(false);
  const selectedGroup =
    sortedGroups.find((g) => g.id === selectedGroupId) ?? sortedGroups[0] ?? null;
  const selectedGroupLabel = selectedGroup
    ? (selectedGroup.name || '').trim() || '이름 없는 그룹'
    : '그룹 선택';

  React.useEffect(() => {
    if (!showGroupPicker) setGroupDropdownOpen(false);
  }, [showGroupPicker]);

  const renderNameChips = (entries: RespondedEntry[], keyPrefix: string) => {
    if (!showName) return null;
    if (entries.length === 0) {
      return (
        <p className="cp-program-slide-communication-no-names">표시할 이름 없음</p>
      );
    }
    return (
      <div className="cp-program-slide-communication-name-chips">
        {entries.map((entry) => {
          const name = displayClassMemberName(entry.member).trim() || '이름 없음';
          return (
            <span
              key={`${keyPrefix}-${entry.member.id}`}
              className="cp-program-slide-communication-name-chip"
            >
              {String(entry.num).padStart(2, '0')} {name}
            </span>
          );
        })}
      </div>
    );
  };

  /** 정답·오답 결과: 같은 응답끼리 묶은 블럭. 이름 표시 시 오른쪽에 이름 칩 */
  const renderAnswerGroupCards = (
    groups: Array<{
      answer: string;
      count: number;
      responders: RespondedEntry[];
    }>,
    keyPrefix: string
  ) => {
    if (!showResponse) return null;
    if (groups.length === 0) return null;
    return (
      <div className="cp-program-slide-communication-answer-cards">
        {groups.map((item) => (
          <div
            key={`${keyPrefix}-${item.answer}`}
            className="cp-program-slide-communication-answer-card"
          >
            <span className="cp-program-slide-communication-answer-card-main">
              {renderChoiceAnswerBadges(item.answer, question, item.answer)}
            </span>
            {showName ? (
              <span className="cp-program-slide-communication-answer-card-names">
                {item.responders.map((entry) => {
                  const name = displayClassMemberName(entry.member).trim() || '이름 없음';
                  return (
                    <span
                      key={`${keyPrefix}-name-${entry.member.id}-${entry.updatedAt}`}
                      className="cp-program-slide-communication-answer-card-name"
                    >
                      {String(entry.num).padStart(2, '0')} {name}
                    </span>
                  );
                })}
              </span>
            ) : item.count > 1 ? (
              <span className="cp-program-slide-communication-answer-count">x{item.count}</span>
            ) : null}
          </div>
        ))}
      </div>
    );
  };

  if (mode === 'student') {
    const trimmedPrompt = (promptText || '').trim();
    const isChoiceQuestion = isChoiceLikeCommunicationQuestion(question);
    const isOxQuestion = question?.type === 'ox';
    const choiceOptions = question?.choices ?? [];
    const selectedChoiceIndexes = isChoiceQuestion
      ? parseCommunicationChoiceSelection(draftBody, choiceOptions)
      : [];
    const canEnter = memberName.trim().length > 0 && entryNumber.trim().length > 0;
    const myNameKey = memberName.trim().toLowerCase();
    const myResponseCount = myNameKey
      ? responses.filter(
          (r) => (r.memberNameSnapshot || '').trim().toLowerCase() === myNameKey
        ).length
      : 0;
    const submitLimitReached = multiResponse
      ? myResponseCount >= PRESENTATION_COMM_MAX_MULTI_RESPONSES
      : myResponseCount >= 1;
    const submitLockNotice = !multiResponse
      ? '응답이 제출 완료되었습니다.'
      : '모든 응답이 제출완료 되었습니다.';
    const canSubmit =
      isOpen &&
      memberName.trim().length > 0 &&
      entryNumber.trim().length > 0 &&
      draftBody.trim().length > 0 &&
      !submitting &&
      !submitLimitReached;
    const inputDisabled = !isOpen || submitLimitReached;

    if (!hasEntered) {
      return (
        <div
          className="cp-program-slide-communication cp-comm-student-view cp-comm-student-gate"
          data-tutorial-slide="question-panel"
        >
          <div className="cp-comm-student-gate-card">
            <h2 className="cp-comm-student-gate-title">학생 참여</h2>
            <p className="cp-comm-student-gate-desc">이름과 입장번호를 입력하고 입장해 주세요.</p>
            <div className="cp-comm-student-identity">
              <div className="cp-comm-student-field">
                <span className="cp-comm-student-label">멤버 이름</span>
                <input
                  id="cp-slide-comm-name"
                  type="text"
                  className="cp-comm-student-input"
                  value={memberName}
                  onChange={(e) => onMemberNameChange?.(e.target.value)}
                  placeholder="이름"
                  autoComplete="off"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && canEnter && !entering) {
                      (e.currentTarget
                        .closest('.cp-comm-student-gate-card')
                        ?.querySelector('.cp-comm-student-enter-btn') as HTMLButtonElement | null)
                        ?.click();
                    }
                  }}
                />
              </div>
              <div className="cp-comm-student-field">
                <span className="cp-comm-student-label">입장번호</span>
                <input
                  id="cp-slide-comm-entry"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  className="cp-comm-student-input"
                  value={entryNumber}
                  onChange={(e) =>
                    onEntryNumberChange?.(e.target.value.replace(/\D/g, '').slice(0, 6))
                  }
                  placeholder="6자리 숫자"
                  autoComplete="off"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && canEnter && !entering) {
                      (e.currentTarget
                        .closest('.cp-comm-student-gate-card')
                        ?.querySelector('.cp-comm-student-enter-btn') as HTMLButtonElement | null)
                        ?.click();
                    }
                  }}
                />
              </div>
            </div>
            <button
              type="button"
              className="cp-comm-student-enter-btn"
              disabled={!canEnter || entering}
              onClick={() => {
                void (async () => {
                  if (!canEnter || entering) return;
                  setEntering(true);
                  setEnterError('');
                  try {
                    await onEnterRequest?.();
                    setHasEntered(true);
                  } catch (e) {
                    setEnterError(
                      e instanceof Error
                        ? e.message
                        : '입장에 실패했습니다. 이름과 입장번호를 확인해 주세요.'
                    );
                  } finally {
                    setEntering(false);
                  }
                })();
              }}
            >
              {entering ? '확인 중…' : '입장하기'}
            </button>
            {enterError ? (
              <p className="cp-comm-student-enter-error" role="alert">
                {enterError}
              </p>
            ) : null}
          </div>
        </div>
      );
    }

    return (
      <div className="cp-program-slide-communication cp-comm-student-view" data-tutorial-slide="question-panel">
        <div className="cp-comm-student-question">
          {isOpen && trimmedPrompt ? (
            trimmedPrompt
          ) : (
            <span className="cp-comm-student-question-waiting">
              선생님이 문제를 재생하면 여기에 표시됩니다.
            </span>
          )}
        </div>

        <div className="cp-comm-student-submit">
          <span className="cp-comm-student-label">내 답변</span>
          {isChoiceQuestion ? (
            <div
              className={'cp-comm-student-choices' + (isOxQuestion ? ' is-ox' : '')}
              role="group"
              aria-label={isOxQuestion ? 'O 또는 X 선택' : '선택지 (복수 선택 가능)'}
            >
              {choiceOptions.map((choice, idx) => {
                const label = (choice.text || '').trim() || `${idx + 1}번`;
                const selected = selectedChoiceIndexes.includes(idx);
                return (
                  <button
                    key={choice.id}
                    type="button"
                    className={
                      'cp-comm-student-choice-btn' + (selected ? ' is-selected' : '')
                    }
                    aria-pressed={selected}
                    disabled={inputDisabled}
                    onClick={() => {
                      const next = isOxQuestion
                        ? selected
                          ? []
                          : [idx]
                        : selected
                          ? selectedChoiceIndexes.filter((i) => i !== idx)
                          : [...selectedChoiceIndexes, idx];
                      onDraftBodyChange?.(
                        formatCommunicationChoiceSelection(choiceOptions, next)
                      );
                    }}
                  >
                    <span
                      className="cp-comm-student-choice-num"
                      style={
                        isOxQuestion
                          ? communicationOxBadgeStyle(idx === 0)
                          : { background: communicationChoiceColor(idx) }
                      }
                    >
                      {selected ? (
                        <span className="cp-comm-student-choice-check">
                          <IconChoiceCheck />
                        </span>
                      ) : null}
                      {isOxQuestion ? label : idx + 1}
                    </span>
                    {isOxQuestion ? null : <span className="cp-comm-student-choice-text">{label}</span>}
                  </button>
                );
              })}
            </div>
          ) : (
            <textarea
              id="cp-slide-comm-body"
              className="cp-comm-student-textarea"
              value={draftBody}
              onChange={(e) => onDraftBodyChange?.(e.target.value)}
              placeholder={
                submitLimitReached
                  ? submitLockNotice
                  : isOpen
                    ? '답변을 입력하세요'
                    : '문제가 재생되면 입력할 수 있어요'
              }
              rows={3}
              disabled={inputDisabled}
            />
          )}
          {multiResponse && !submitLimitReached && myResponseCount > 0 ? (
            <span className="cp-comm-student-submit-remain" role="status">
              남은 제출 {PRESENTATION_COMM_MAX_MULTI_RESPONSES - myResponseCount}회
              (최대 {PRESENTATION_COMM_MAX_MULTI_RESPONSES}회)
            </span>
          ) : null}
          <div className="cp-comm-student-submit-actions">
            <button
              type="button"
              className="cp-comm-student-submit-btn"
              onClick={() => onSubmitResponse?.()}
              disabled={!canSubmit}
            >
              <IconAirplane />
              <span>{submitting ? '제출 중…' : '답변 제출'}</span>
            </button>
            {submitLimitReached || submitMessage ? (
              <p className="cp-comm-student-submit-done" role="status" aria-live="polite">
                {submitLimitReached ? submitLockNotice : submitMessage}
              </p>
            ) : null}
            {submitError ? (
              <p className="cp-comm-student-submit-error" role="alert">
                {submitError}
              </p>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={
        'cp-program-slide-communication' + (seatMode ? ' is-seat-layout' : '') + (listFit ? ' is-fit' : '')
      }
      ref={rootRef}
      data-tutorial-slide="question-panel"
      style={
        { ['--cp-comm-student-font-scale' as any]: studentFontScale } as React.CSSProperties
      }
    >
      <div className="cp-program-slide-communication-toolbar">
        {mode === 'teacher' ? (
        <div className="cp-program-slide-communication-top-row">
          <div className="cp-program-slide-communication-groups-wrap">
            {showGroupPicker ? (
              <div
                className={
                  'cp-program-slide-communication-group-select' +
                  (groupDropdownOpen ? ' is-open' : '')
                }
                data-tutorial-slide="question-group"
              >
                <button
                  type="button"
                  className="cp-program-slide-communication-group-trigger"
                  aria-haspopup="listbox"
                  aria-expanded={groupDropdownOpen}
                  aria-label="멤버 그룹"
                  title={selectedGroupLabel}
                  onClick={() => setGroupDropdownOpen((v) => !v)}
                >
                  <span className="cp-program-slide-communication-group-trigger-name">
                    {selectedGroupLabel}
                  </span>
                  <svg
                    className="cp-program-slide-communication-group-trigger-chevron"
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M2.5 4.5L6 8l3.5-3.5"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                {groupDropdownOpen ? (
                  <>
                    <div
                      className="cp-program-slide-communication-group-overlay"
                      aria-hidden
                      onClick={() => setGroupDropdownOpen(false)}
                    />
                    <ul
                      className="cp-program-slide-communication-group-menu"
                      role="listbox"
                      aria-label="멤버 그룹 목록"
                    >
                      {sortedGroups.map((g) => {
                        const active = g.id === (selectedGroup?.id ?? selectedGroupId);
                        const label = (g.name || '').trim() || '이름 없는 그룹';
                        return (
                          <li key={g.id} role="option" aria-selected={active}>
                            <button
                              type="button"
                              className={
                                'cp-program-slide-communication-group-option' +
                                (active ? ' is-selected' : '')
                              }
                              title={label}
                              onClick={() => {
                                onSelectGroup?.(g.id);
                                setGroupDropdownOpen(false);
                              }}
                            >
                              <span className="cp-program-slide-communication-group-option-name">
                                {label}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </>
                ) : null}
              </div>
            ) : null}
            <div
              className="cp-program-slide-communication-view-mode"
              role="group"
              aria-label="결과 보기"
              data-tutorial-slide="question-view-mode"
            >
              {(
                [
                  { id: 'students' as const, full: '멤버별 응답 결과', short: '응답' },
                  { id: 'accuracy' as const, full: '정답·오답 결과', short: '정.오답' },
                  { id: 'breakdown' as const, full: '항목별 결과', short: '항목' },
                ] as const
              ).filter((opt) => !(isEssayQuestion && opt.id === 'accuracy')).map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  className={
                    'cp-program-slide-communication-view-btn' +
                    (resultViewMode === opt.id ? ' is-active' : '')
                  }
                  aria-pressed={resultViewMode === opt.id}
                  title={opt.full}
                  onClick={() => handleResultViewModeChange(opt.id)}
                >
                  <span className="cp-program-slide-communication-view-btn-full">{opt.full}</span>
                  <span className="cp-program-slide-communication-view-btn-short">{opt.short}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="cp-program-slide-communication-sort-row">
            <div
              className="cp-program-slide-communication-sort"
              role="group"
              aria-label="정렬"
              data-tutorial-slide="question-sort"
            >
              {resultViewMode === 'students'
                ? (
                    [
                      { id: 'number' as const, label: '번호순', disabled: !showName },
                      { id: 'response' as const, label: '응답순', disabled: false },
                      { id: 'random' as const, label: '무작위', disabled: false },
                      { id: 'seat' as const, label: '자리배치', disabled: false },
                    ] as const
                  ).map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      className={
                        'cp-program-slide-communication-sort-btn' +
                        (sortOrder === opt.id ? ' is-active' : '') +
                        (opt.disabled ? ' is-disabled' : '')
                      }
                      disabled={opt.disabled}
                      aria-pressed={sortOrder === opt.id}
                      onClick={() => handleSortClick(opt.id)}
                    >
                      {opt.label}
                    </button>
                  ))
                : null}
              {resultViewMode === 'accuracy'
                ? (
                    [
                      {
                        id: 'response' as const,
                        label: '응답순',
                        disabled: !showResponse,
                      },
                      {
                        id: 'alphabetical' as const,
                        label: '문자순',
                        disabled: !showResponse,
                      },
                    ] as const
                  ).map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      className={
                        'cp-program-slide-communication-sort-btn' +
                        (accuracySortOrder === opt.id ? ' is-active' : '') +
                        (opt.disabled ? ' is-disabled' : '')
                      }
                      disabled={opt.disabled}
                      aria-pressed={accuracySortOrder === opt.id}
                      onClick={() => {
                        if (opt.disabled) return;
                        setAccuracySortOrder(opt.id);
                      }}
                    >
                      {opt.label}
                    </button>
                  ))
                : null}
              {resultViewMode === 'breakdown'
                ? (
                    [
                      {
                        id: 'correctness' as const,
                        label: '정답순',
                        disabled: !showAnswer,
                      },
                      { id: 'response' as const, label: '응답순', disabled: false },
                      { id: 'alphabetical' as const, label: '문자순', disabled: false },
                      { id: 'count' as const, label: '인원순', disabled: false },
                    ] as const
                  ).map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      className={
                        'cp-program-slide-communication-sort-btn' +
                        (breakdownSortOrder === opt.id ? ' is-active' : '') +
                        (opt.disabled ? ' is-disabled' : '')
                      }
                      disabled={opt.disabled}
                      aria-pressed={breakdownSortOrder === opt.id}
                      onClick={() => {
                        if (opt.disabled) return;
                        setBreakdownSortOrder(opt.id);
                      }}
                    >
                      {opt.label}
                    </button>
                  ))
                : null}
            </div>
          </div>

          <div className="cp-program-slide-communication-controls">
            <div
              className="cp-program-slide-communication-options"
              role="group"
              aria-label="표시 옵션"
              data-tutorial-slide="question-options"
            >
              <button
                type="button"
                className={
                  'cp-program-slide-communication-option-toggle' +
                  (showResponse ? ' is-on' : '') +
                  (responseToggleLocked ? ' is-locked' : '')
                }
                aria-label={showResponse ? '응답 표시' : '응답 숨김'}
                aria-pressed={showResponse}
                disabled={responseToggleLocked}
                title={
                  responseToggleLocked
                    ? '항목별 결과에서는 응답 표시를 변경할 수 없습니다'
                    : undefined
                }
                onClick={() => {
                  if (responseToggleLocked) return;
                  setShowResponse((v) => !v);
                }}
              >
                <span
                  className={
                    'cp-program-slide-communication-option-side' + (!showResponse ? ' is-active' : '')
                  }
                >
                  <span>응답</span>
                  <IconEyeClose />
                </span>
                <span
                  className={
                    'cp-program-slide-communication-option-side' + (showResponse ? ' is-active' : '')
                  }
                >
                  <span>응답</span>
                  <IconEyeOpen />
                </span>
              </button>
              <button
                type="button"
                className={
                  'cp-program-slide-communication-option-toggle' + (showName ? ' is-on' : '')
                }
                aria-label={showName ? '이름 표시' : '이름 숨김'}
                aria-pressed={showName}
                onClick={() => setShowName((v) => !v)}
              >
                <span
                  className={
                    'cp-program-slide-communication-option-side' + (!showName ? ' is-active' : '')
                  }
                >
                  <span>이름</span>
                  <IconEyeClose />
                </span>
                <span
                  className={
                    'cp-program-slide-communication-option-side' + (showName ? ' is-active' : '')
                  }
                >
                  <span>이름</span>
                  <IconEyeOpen />
                </span>
              </button>
              <button
                type="button"
                className={
                  'cp-program-slide-communication-option-toggle' +
                  (showAnswer ? ' is-on' : '') +
                  (answerToggleLocked ? ' is-locked' : '')
                }
                aria-label={showAnswer ? '정답 표시' : '정답 숨김'}
                aria-pressed={showAnswer}
                disabled={answerToggleLocked}
                title={
                  answerToggleLocked
                    ? '정답·오답 결과에서는 정답 표시를 변경할 수 없습니다'
                    : undefined
                }
                onClick={() => {
                  if (answerToggleLocked) return;
                  setShowAnswer((v) => !v);
                }}
              >
                <span
                  className={
                    'cp-program-slide-communication-option-side' + (!showAnswer ? ' is-active' : '')
                  }
                >
                  <span>정답</span>
                  <IconEyeClose />
                </span>
                <span
                  className={
                    'cp-program-slide-communication-option-side' + (showAnswer ? ' is-active' : '')
                  }
                >
                  <span>정답</span>
                  <IconEyeOpen />
                </span>
              </button>
            </div>
            <div
              className="cp-program-slide-communication-font-scale"
              role="group"
              aria-label="학생 카드 폰트 크기"
              data-tutorial-slide="question-font"
            >
              <button
                type="button"
                className="cp-program-slide-communication-font-scale-btn"
                aria-label="폰트 작게"
                title={`폰트 크기 ${(studentFontScale * 100).toFixed(0)}%`}
                onClick={() => adjustStudentFontScale(-FONT_SCALE_STEP)}
                disabled={studentFontScale <= FONT_SCALE_MIN + 1e-9}
              >
                <svg width="14" height="14" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                  <path d="M2 6h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
              <button
                type="button"
                className="cp-program-slide-communication-font-scale-btn"
                aria-label="폰트 크게"
                title={`폰트 크기 ${(studentFontScale * 100).toFixed(0)}%`}
                onClick={() => adjustStudentFontScale(FONT_SCALE_STEP)}
                disabled={studentFontScale >= FONT_SCALE_MAX - 1e-9}
              >
                <svg width="14" height="14" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                  <path d="M6 2v8M2 6h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            </div>
            {onExportCsv || onExportPdf ? (
              <div
                className="cp-program-slide-communication-export"
                role="group"
                aria-label="질문·응답 전체 다운로드"
                data-tutorial-slide="question-export"
              >
                {onExportCsv ? (
                  <button
                    type="button"
                    className="cp-program-slide-communication-export-btn"
                    aria-label="전체 질문·응답 엑셀로 다운로드"
                    title="모든 질문·응답을 엑셀(.xlsx)로 다운로드"
                    disabled={exportingCsv}
                    onClick={handleExportCommunicationCsv}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path
                        d="M12 3v12m0 0-4-4m4 4 4-4M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <span>{exportingCsv ? '엑셀 생성 중…' : '엑셀'}</span>
                  </button>
                ) : null}
                {onExportPdf ? (
                  <button
                    type="button"
                    className="cp-program-slide-communication-export-btn"
                    aria-label="전체 질문·응답 PDF로 다운로드"
                    title="모든 질문·응답을 A4 PDF로 다운로드"
                    disabled={exportingPdf}
                    onClick={handleExportCommunicationPdf}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path
                        d="M12 3v12m0 0-4-4m4 4 4-4M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <span>{exportingPdf ? 'PDF 생성 중…' : 'PDF'}</span>
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
        ) : null}

      <header className="cp-program-slide-communication-header">
        {mode === 'teacher' ? (
          <div className="cp-program-slide-communication-prompt-center">
            <div className="cp-program-slide-communication-prompt-row">
              {onOpenMonitor || showDisabledMonitorButton || onOpenConnect || playControl ? (
                <div className="cp-program-slide-communication-prompt-tools">
                  {onOpenMonitor ? (
                    <button
                      type="button"
                      className="cp-program-slide-communication-qnav-btn cp-program-slide-communication-monitor-btn"
                      data-tutorial-slide="question-monitor"
                      onClick={onOpenMonitor}
                      aria-label="모니터링"
                      title="원래 탭에서 질문 보드를 모니터링합니다"
                    >
                      <img src="/Monitor2.svg" alt="" width={22} height={22} aria-hidden="true" />
                    </button>
                  ) : showDisabledMonitorButton ? (
                    <button
                      type="button"
                      className="cp-program-slide-communication-qnav-btn cp-program-slide-communication-monitor-btn"
                      disabled
                      aria-label="모니터링"
                      title="모니터링 화면에서는 사용할 수 없습니다"
                    >
                      <img src="/Monitor2.svg" alt="" width={22} height={22} aria-hidden="true" />
                    </button>
                  ) : null}
                  {onOpenConnect ? (
                    <button
                      type="button"
                      className="cp-program-slide-communication-qnav-btn cp-program-slide-communication-connect-btn"
                      onClick={onOpenConnect}
                      disabled={connectLoading}
                      aria-label="연결"
                      title="개별 디바이스 프레젠테이션 연결"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <rect x="3.5" y="3.5" width="6.5" height="6.5" rx="1.2" stroke="currentColor" strokeWidth="1.8" />
                        <rect x="14" y="3.5" width="6.5" height="6.5" rx="1.2" stroke="currentColor" strokeWidth="1.8" />
                        <rect x="3.5" y="14" width="6.5" height="6.5" rx="1.2" stroke="currentColor" strokeWidth="1.8" />
                        <path d="M14 14h2.5v2.5H14zM18 18h2.5v2.5H18zM14 18.5h2M18.5 14h2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                        <rect x="5.9" y="5.9" width="1.7" height="1.7" fill="currentColor" />
                        <rect x="16.4" y="5.9" width="1.7" height="1.7" fill="currentColor" />
                        <rect x="5.9" y="16.4" width="1.7" height="1.7" fill="currentColor" />
                      </svg>
                      <span>{connectLoading ? '연결…' : '연결'}</span>
                    </button>
                  ) : null}
                  {playControl ? (
                <div
                  className="cp-comm-play-control"
                  role="group"
                  aria-label="문제 재생 컨트롤"
                  data-tutorial-slide="question-play-control"
                >
                  <button
                    type="button"
                    className={
                      'cp-comm-play-control-btn cp-comm-play-control-multi cp-comm-tooltip' +
                      (playControl.multiResponse ? ' is-on' : '')
                    }
                    aria-pressed={playControl.multiResponse}
                    aria-label={playControl.multiResponse ? '복수 응답' : '단일 응답'}
                    data-tooltip={playControl.multiResponse ? '복수 응답' : '단일 응답'}
                    onClick={playControl.onToggleMultiResponse}
                  >
                    {playControl.multiResponse ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <rect
                          x="9.5"
                          y="3.5"
                          width="11"
                          height="11"
                          rx="2.5"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.7"
                        />
                        <rect
                          x="3.5"
                          y="9.5"
                          width="11"
                          height="11"
                          rx="2.5"
                          fill="var(--color-surface, #fff)"
                          stroke="currentColor"
                          strokeWidth="1.7"
                        />
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <rect
                          x="5"
                          y="5"
                          width="14"
                          height="14"
                          rx="2.5"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.7"
                        />
                      </svg>
                    )}
                  </button>
                  <button
                    type="button"
                    className={
                      'cp-comm-play-control-btn cp-comm-play-control-play cp-comm-tooltip' +
                      (playControl.isOpen ? ' is-playing' : '')
                    }
                    disabled={!playControl.canPlay}
                    aria-label={playControl.isOpen ? '정지' : '재생'}
                    data-tooltip={
                      playControl.isOpen
                        ? '정지'
                        : !playControl.canPlay && playControl.disabledReason
                          ? playControl.disabledReason
                          : '재생'
                    }
                    onClick={playControl.isOpen ? playControl.onStop : playControl.onPlay}
                  >
                    {playControl.isOpen ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <rect x="6" y="6" width="12" height="12" rx="1.5" fill="currentColor" />
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="M8.5 5.2v13.6a1 1 0 0 0 1.53.85l10.9-6.8a1 1 0 0 0 0-1.7l-10.9-6.8a1 1 0 0 0-1.53.85Z" fill="currentColor" />
                      </svg>
                    )}
                  </button>
                </div>
              ) : null}
                </div>
              ) : null}
              {questionNav ? (
                  <div
                  className="cp-program-slide-communication-qnav"
                  role="group"
                  aria-label="질문 탐색"
                  data-tutorial-slide="question-nav"
                >
                  <button
                    type="button"
                    className="cp-program-slide-communication-qnav-btn"
                    aria-label="이전 질문"
                    disabled={!questionNav.canPrev}
                    onClick={questionNav.onPrev}
                  >
                    <span className="cp-program-slide-communication-qnav-glyph" aria-hidden="true">
                      ‹
                    </span>
                  </button>
                  <button
                    type="button"
                    className="cp-program-slide-communication-qnav-btn"
                    aria-label="다음 질문"
                    disabled={!questionNav.canNext}
                    onClick={questionNav.onNext}
                  >
                    <span className="cp-program-slide-communication-qnav-glyph" aria-hidden="true">
                      ›
                    </span>
                  </button>
                  {questionNav.onAdd ? (
                    <button
                      type="button"
                      className="cp-program-slide-communication-qnav-btn"
                      aria-label="새 질문 추가"
                      title="새 질문 추가"
                      disabled={!questionNav.canAdd}
                      onClick={questionNav.onAdd}
                    >
                      <span className="cp-program-slide-communication-qnav-glyph" aria-hidden="true">
                        +
                      </span>
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className={
                      'cp-program-slide-communication-qnav-btn is-danger is-position-delete' +
                      (questionNav.positionDisplayOnly ? ' is-readonly-display' : '')
                    }
                    aria-label={
                      questionNav.canDelete
                        ? `${questionNav.currentIndex}/${questionNav.totalCount}. 호버하면 삭제`
                        : `${questionNav.currentIndex}/${questionNav.totalCount}`
                    }
                    title={
                      questionNav.canDelete
                        ? `${questionNav.currentIndex}/${questionNav.totalCount} · 호버하여 삭제`
                        : `${questionNav.currentIndex}/${questionNav.totalCount}`
                    }
                    disabled={!questionNav.canDelete}
                    onClick={questionNav.onDeleteRequest}
                  >
                    <span className="cp-program-slide-communication-qnav-position" aria-hidden="true">
                      {questionNav.currentIndex}/{questionNav.totalCount}
                    </span>
                    <svg
                      className="cp-program-slide-communication-qnav-trash"
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M4 7h16M10 11v6M14 11v6M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              ) : null}
            </div>
            <button
              type="button"
              className={
                'cp-program-slide-communication-prompt-display' +
                (hasSavedPrompt ? '' : ' is-placeholder')
              }
              data-tutorial-slide="question-prompt"
              onClick={() => openQuestionModal(hasSavedPrompt ? 'view' : 'edit')}
              title="클릭하여 문제 상세"
            >
              <span className="cp-program-slide-communication-prompt-display-text">
                {(promptText || '').trim() || '질문 / 안내사항을 입력하세요'}
              </span>
            </button>
          </div>
        ) : (
          <div className="cp-program-slide-communication-prompt-readonly">
            {(promptText || '').trim() ? promptText : '안내 문구가 아직 없습니다.'}
          </div>
        )}
      </header>

      {deleteQuestionConfirmOpen ? (
        <div
          className="cp-program-slide-communication-delete-overlay"
          role="presentation"
          onClick={onCancelDeleteQuestion}
        >
          <div
            className="cp-program-slide-communication-delete-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cp-comm-q-delete-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="cp-comm-q-delete-title">질문을 삭제할까요?</h3>
            <p>
              이 질문과 학생 응답이 함께 삭제됩니다. 이 작업은 되돌릴 수 없습니다.
            </p>
            <div className="cp-program-slide-communication-delete-actions">
              <button type="button" className="cp-btn-secondary" onClick={onCancelDeleteQuestion}>
                취소
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                disabled={deletingQuestion}
                onClick={onConfirmDeleteQuestion}
              >
                {deletingQuestion ? '삭제 중…' : '삭제'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      </div>

      <div className="cp-program-slide-communication-students" ref={studentsRef}>
      {resultViewMode === 'accuracy' ? (
        <div className="cp-program-slide-communication-accuracy-grid">
          <article className="cp-program-slide-communication-accuracy-card is-correct">
            <h3 className="cp-program-slide-communication-accuracy-title">정답자</h3>
            <div className="cp-program-slide-communication-accuracy-bar-wrap">
              <div
                className="cp-program-slide-communication-accuracy-bar is-correct"
                style={{
                  width: `${
                    respondedEntries.length
                      ? (accuracyStats.correct.length / respondedEntries.length) * 100
                      : 0
                  }%`,
                }}
              />
            </div>
            <p className="cp-program-slide-communication-accuracy-count">
              {accuracyStats.correct.length}명
            </p>
            {showName && !showResponse ? renderNameChips(accuracyStats.correct, 'correct') : null}
            {showName && showResponse && accuracyStats.correct.length === 0 ? (
              <p className="cp-program-slide-communication-no-names">표시할 이름 없음</p>
            ) : null}
            {renderAnswerGroupCards(correctAnswers, 'correct')}
          </article>
          <article className="cp-program-slide-communication-accuracy-card is-incorrect">
            <h3 className="cp-program-slide-communication-accuracy-title">오답자</h3>
            <div className="cp-program-slide-communication-accuracy-bar-wrap">
              <div
                className="cp-program-slide-communication-accuracy-bar is-incorrect"
                style={{
                  width: `${
                    respondedEntries.length
                      ? (accuracyStats.incorrect.length / respondedEntries.length) * 100
                      : 0
                  }%`,
                }}
              />
            </div>
            <p className="cp-program-slide-communication-accuracy-count">
              {accuracyStats.incorrect.length}명
            </p>
            {showName && !showResponse ? renderNameChips(accuracyStats.incorrect, 'incorrect') : null}
            {showName && showResponse && accuracyStats.incorrect.length === 0 ? (
              <p className="cp-program-slide-communication-no-names">표시할 이름 없음</p>
            ) : null}
            {renderAnswerGroupCards(incorrectAnswers, 'incorrect')}
          </article>
        </div>
      ) : resultViewMode === 'breakdown' ? (
        <div className="cp-program-slide-communication-breakdown-list">
          {breakdownStats.length === 0 ? (
            <p className="cp-program-slide-communication-empty">표시할 응답이 없습니다.</p>
          ) : (
            breakdownStats.map((item) => {
              const percentage = respondedEntries.length
                ? (item.count / respondedEntries.length) * 100
                : 0;
              const isCorrect = isCommunicationLabelCorrect(
                item.label,
                question,
                answerText,
                showAnswer
              );
              return (
                <article
                  key={item.label}
                  className="cp-program-slide-communication-breakdown-card"
                >
                  <div className="cp-program-slide-communication-breakdown-info">
                    <div className="cp-program-slide-communication-breakdown-label-wrap">
                      {showAnswer ? (
                        <span
                          className={
                            'cp-program-slide-communication-breakdown-ox' +
                            (isCorrect ? ' is-o' : ' is-x')
                          }
                          aria-label={isCorrect ? '정답' : '오답'}
                        >
                          {renderOxIcon(isCorrect)}
                        </span>
                      ) : null}
                      <span className="cp-program-slide-communication-breakdown-label">
                        {renderChoiceAnswerBadges(item.label, question, item.label)}
                      </span>
                    </div>
                    <span className="cp-program-slide-communication-breakdown-count">
                      {item.count}명
                    </span>
                  </div>
                  <div className="cp-program-slide-communication-breakdown-bar-wrap">
                    <div
                      className="cp-program-slide-communication-breakdown-bar"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  {renderNameChips(item.responders, `bd-${item.label}`)}
                </article>
              );
            })
          )}
        </div>
      ) : sortOrder === 'seat' && seatViewData.positioned.length === 0 ? (
        <p className="cp-program-slide-communication-empty-seat">자리배치를 먼저 해주세요</p>
      ) : seatMode ? (
        <div className="cp-program-slide-communication-seat-area">
          <div className="cp-program-slide-communication-seat-scroll">
            <div
              className="cp-program-slide-communication-seat-grid"
              style={{
                gridTemplateColumns: seatViewData.colTemplate,
                gridTemplateRows: seatViewData.rowTemplate,
                ['--cp-comm-seat-cols' as string]: String(
                  seatViewData.colTemplate.trim().split(/\s+/).filter(Boolean).length || 1
                ),
                ['--cp-comm-seat-rows' as string]: String(
                  seatViewData.rowTemplate.trim().split(/\s+/).filter(Boolean).length || 1
                ),
              }}
            >
              {seatViewData.positioned.map(({ member, gridRow, gridCol }) => {
                const memberResponses = responsesByMemberAll.get(member.id) ?? [];
                const pageIndex = memberResponsePageIndex[member.id] ?? memberResponses.length - 1;
                const num = memberNumberById.get(member.id) ?? 0;
                return (
                  <div
                    key={member.id}
                    className="cp-program-slide-communication-seat-cell"
                    style={{
                      gridRow: `${gridRow} / span 2`,
                      gridColumn: `${gridCol} / span 2`,
                    }}
                  >
                    {renderMemberCard({
                      member,
                      num,
                      responses: memberResponses,
                      pageIndex,
                      onPageChange: (next) => setMemberResponsePage(member.id, next),
                      showName,
                      showResponse,
                      showAnswer,
                      question,
                      answerText,
                    })}
                  </div>
                );
              })}
            </div>
          </div>
          <div className="cp-program-slide-communication-seat-rotate-wrap is-bottom">
            <button
              type="button"
              className="cp-program-slide-communication-seat-rotate-btn"
              title="자리 배치 180도 회전"
              aria-label="자리 배치 180도 회전"
              onClick={() => setIsSeatRotated((v) => !v)}
            >
              <span aria-hidden>↻</span>
              <span>180</span>
            </button>
          </div>
          {seatViewData.unseated.length > 0 ? (
            <>
              <p className="cp-program-slide-communication-unseated-title">자리 미지정 멤버</p>
              <div className="cp-program-slide-communication-board is-list-mode">
                {seatViewData.unseated.map((member) => {
                  const memberResponses = responsesByMemberAll.get(member.id) ?? [];
                  const pageIndex = memberResponsePageIndex[member.id] ?? memberResponses.length - 1;
                  const num = memberNumberById.get(member.id) ?? 0;
                  return (
                    <React.Fragment key={member.id}>
                      {renderMemberCard({
                        member,
                        num,
                        responses: memberResponses,
                        pageIndex,
                        onPageChange: (next) => setMemberResponsePage(member.id, next),
                        showName,
                        showResponse,
                        showAnswer,
                        question,
                        answerText,
                      })}
                    </React.Fragment>
                  );
                })}
              </div>
            </>
          ) : null}
        </div>
      ) : (
        <div
          className="cp-program-slide-communication-board is-list-mode"
          style={listFit ? { gridTemplateRows: `repeat(${fitRows}, minmax(0, 1fr))` } : undefined}
        >
          {sortedMembers.length === 0 ? (
            <p className="cp-program-slide-communication-empty">표시할 클래스 멤버가 없습니다.</p>
          ) : (
            sortedMembers.map((member) => {
              const memberResponses = responsesByMemberAll.get(member.id) ?? [];
              const pageIndex = memberResponsePageIndex[member.id] ?? memberResponses.length - 1;
              const num = memberNumberById.get(member.id) ?? 0;
              return (
                <React.Fragment key={member.id}>
                  {renderMemberCard({
                    member,
                    num,
                    responses: memberResponses,
                    pageIndex,
                    onPageChange: (next) => setMemberResponsePage(member.id, next),
                    showName,
                    showResponse,
                    showAnswer,
                    question,
                    answerText,
                  })}
                </React.Fragment>
              );
            })
          )}
        </div>
      )}

      </div>

      {mode === 'teacher' ? (
        <ProgramSlideCommunicationQuestionModal
          open={questionModalOpen}
          initialQuestion={question}
          initialMode={questionModalMode}
          readOnly={questionDetailReadOnly}
          saving={false}
          saveError={null}
          onClose={() => setQuestionModalOpen(false)}
          onSave={(nextQuestion, nextPrompt, nextAnswer) => {
            if (onSaveQuestion) {
              // 모니터링 팝업: 저장 즉시 질문 페이지 저장 핸들러로 DB 반영(원격)
              void onSaveQuestion(nextQuestion, nextPrompt, nextAnswer);
              setQuestionModalOpen(false);
              return;
            }
            // 모달은 페이지 초안에만 반영 → dirty 감지 후 FAB「저장하기」로 DB 반영
            onClearQuestionSaveError?.();
            onQuestionChange?.(nextQuestion);
            onPromptChange?.(nextPrompt);
            onAnswerChange?.(nextAnswer);
            setQuestionModalOpen(false);
          }}
        />
      ) : null}
    </div>
  );
};

export default ProgramSlideCommunicationPanel;
